if (!customElements.get("countdown-timer")) {
  customElements.define(
    "countdown-timer",
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();
        // Bind the updateCountdown method to ensure the correct this context
        this.updateCountdown = this.updateCountdown.bind(this);
      }

      connectedCallback() {
        // Initialize properties
        this._initProperties();
        if (!this.timerDisplay) {
          console.error("Timer display element not found.");
          return;
        }
        if (isNaN(this.endDate)) {
          if (this.timerError) {
            this.timerError.innerHTML = `${this.translations.invalidDate}`;
          }
          return;
        }
        // Render static structure once and start the timer
        this.renderStatic();
        this.updateCountdown();
        this.interval = setInterval(this.updateCountdown, 1000);
      }

      // Update the countdown timer display
      updateCountdown() {
        const now = new Date().getTime();
        const distance = this.endDate - now;

        if (distance < 0) {
          this.timerDisplay.innerHTML = `<span class="expired-message">${this.expiredMessage}</span>`;
          clearInterval(this.interval);
          return;
        }

        const days = String(
          Math.floor(distance / (1000 * 60 * 60 * 24)),
        ).padStart(2, "0");
        const hours = String(
          Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        ).padStart(2, "0");
        const minutes = String(
          Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        ).padStart(2, "0");
        const seconds = String(
          Math.floor((distance % (1000 * 60)) / 1000),
        ).padStart(2, "0");
        if (this.valueDays && this.valueHours && this.valueMinutes && this.valueSeconds) {
          // Update per-digit spans for each value
          this._setDigits(this.valueDays, days);
          this._setDigits(this.valueHours, hours);
          this._setDigits(this.valueMinutes, minutes);
          this._setDigits(this.valueSeconds, seconds);
        } else {
          this.timerDisplay.innerHTML = `
            <div class="wt-counter__item wt-counter__item--days">
              <div class="wt-counter__item__value">${this._stringToDigits(days)}</div>
              <div class="wt-counter__item__label">${this.translations.days}</div>
            </div>
            <div class="wt-counter__item wt-counter__item--hours">
              <div class="wt-counter__item__value">${this._stringToDigits(hours)}</div>
              <div class="wt-counter__item__label">${this.translations.hours}</div>
            </div>
            <div class="wt-counter__item wt-counter__item--minutes">
              <div class="wt-counter__item__value">${this._stringToDigits(minutes)}</div>
              <div class="wt-counter__item__label">${this.translations.minutes}</div>
            </div>
            <div class="wt-counter__item wt-counter__item--seconds">
              <div class="wt-counter__item__value">${this._stringToDigits(seconds)}</div>
              <div class="wt-counter__item__label">${this.translations.seconds}</div>
            </div>`;
        }
      }

      disconnectedCallback() {
        // Clear the interval when the element is removed from the DOM
        clearInterval(this.interval);
      }

      renderStatic() {
        this.timerDisplay.innerHTML = `
          <div class="wt-counter__item wt-counter__item--days">
            <div class="wt-counter__item__value js-countdown-days"></div>
            <div class="wt-counter__item__label">${this.translations.days}</div>
          </div>
          <div class="wt-counter__item wt-counter__item--hours">
            <div class="wt-counter__item__value js-countdown-hours"></div>
            <div class="wt-counter__item__label">${this.translations.hours}</div>
          </div>
          <div class="wt-counter__item wt-counter__item--minutes">
            <div class="wt-counter__item__value js-countdown-minutes"></div>
            <div class="wt-counter__item__label">${this.translations.minutes}</div>
          </div>
          <div class="wt-counter__item wt-counter__item--seconds">
            <div class="wt-counter__item__value js-countdown-seconds"></div>
            <div class="wt-counter__item__label">${this.translations.seconds}</div>
          </div>`;
        this.valueDays = this.timerDisplay.querySelector('.js-countdown-days');
        this.valueHours = this.timerDisplay.querySelector('.js-countdown-hours');
        this.valueMinutes = this.timerDisplay.querySelector('.js-countdown-minutes');
        this.valueSeconds = this.timerDisplay.querySelector('.js-countdown-seconds');
      }

      // Ensure container has the right number of digit spans and set their content
      _setDigits(container, value) {
        if (!container) return;
        const str = String(value);
        let digits = Array.from(container.querySelectorAll('.digit'));
        if (digits.length !== str.length) {
          // Rebuild spans to match the current length
          container.innerHTML = this._stringToDigits(str);
          digits = Array.from(container.querySelectorAll('.digit'));
        }
        for (let i = 0; i < str.length; i++) {
          if (digits[i]) digits[i].textContent = str[i];
        }
      }

      // Convert a string number into span-wrapped digits
      _stringToDigits(str) {
        return String(str)
          .split('')
          .map(ch => `<span class="digit">${ch}</span>`)
          .join('');
      }

      // Initialize component properties
      _initProperties() {
        const endDateString = this.getAttribute("end-date");
        this.endDate = Date.parse(endDateString.replace(/-/g, "/"));
        this.expiredMessage = this.getAttribute("expired-message");
        this.timerDisplay = this.querySelector(".wt-countdown-timer__display");
        this.timerError = this.querySelector(".wt-countdown-timer__error");
        const useLongLabels = this.hasAttribute("data-long-labels");
        this.translations = {
          days: useLongLabels
            ? this.getAttribute("data-translation-days-long")
            : this.getAttribute("data-translation-days") || "d",
          hours: useLongLabels
            ? this.getAttribute("data-translation-hours-long")
            : this.getAttribute("data-translation-hours") || "h",
          minutes: useLongLabels
            ? this.getAttribute("data-translation-minutes-long")
            : this.getAttribute("data-translation-minutes") || "m",
          seconds: useLongLabels
            ? this.getAttribute("data-translation-seconds-long")
            : this.getAttribute("data-translation-seconds") || "s",
          invalidDate:
            this.getAttribute("data-translation-invalid-date") ||
            "Invalid date format. Please enter the date and time in the format YYYY-MM-DD HH:MM.",
        };
      }
    },
  );
}
